import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Auto-logout après inactivité pour le back-office.
 * - Active uniquement si un token admin est présent dans localStorage.
 * - Détecte mouse / clavier / touch / scroll / visibilitychange.
 * - Affiche un avertissement `warnMs` millisecondes avant la déconnexion.
 * - Synchronisation multi-onglets via localStorage `dtsp_admin_last_activity`.
 *
 * @param {object} opts
 * @param {number} opts.idleMs      — durée totale d'inactivité avant logout (défaut 15 min)
 * @param {number} opts.warnMs      — avertissement avant logout (défaut 60 s)
 * @param {string} opts.tokenKey    — clé localStorage du token
 * @param {function} opts.onLogout  — callback (doit invalider la session + rediriger)
 */
export default function useIdleLogout({
  idleMs = 15 * 60 * 1000,
  warnMs = 60 * 1000,
  tokenKey = "dtsp_admin_token",
  onLogout,
} = {}) {
  const ACTIVITY_KEY = "dtsp_admin_last_activity";
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(warnMs);
  const timerRef = useRef(null);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);

  const stampActivity = useCallback(() => {
    try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch {}
  }, []);

  const reset = useCallback(() => {
    stampActivity();
    setShowWarning(false);
    setRemaining(warnMs);
  }, [stampActivity, warnMs]);

  useEffect(() => {
    // Si pas connecté, on ne fait rien.
    const hasToken = () => !!(localStorage.getItem(tokenKey) || "");
    if (!hasToken()) return;

    // Initialise le timestamp si absent
    if (!localStorage.getItem(ACTIVITY_KEY)) stampActivity();

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    const onEvent = () => reset();
    events.forEach(e => window.addEventListener(e, onEvent, { passive: true }));

    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) {
        setShowWarning(false);
        setRemaining(warnMs);
      }
    };
    window.addEventListener("storage", onStorage);

    const tick = () => {
      if (!hasToken()) return;
      const last = Number(localStorage.getItem(ACTIVITY_KEY) || Date.now());
      const idle = Date.now() - last;
      if (idle >= idleMs) {
        // Logout
        try { localStorage.removeItem(ACTIVITY_KEY); } catch {}
        setShowWarning(false);
        if (onLogoutRef.current) onLogoutRef.current();
        return;
      }
      if (idle >= idleMs - warnMs) {
        setShowWarning(true);
        setRemaining(Math.max(0, idleMs - idle));
      } else if (showWarning) {
        setShowWarning(false);
      }
    };
    timerRef.current = setInterval(tick, 1000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onEvent));
      window.removeEventListener("storage", onStorage);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idleMs, warnMs, tokenKey, reset, stampActivity]);

  return { showWarning, remaining, stayActive: reset };
}
